import { useNavigate } from 'react-router-dom'
import { ExternalLink } from 'lucide-react'
import type { Lead } from '@/types'
import { formatCurrency } from '@/lib/pricing'
import { StatusBadge } from './StatusBadge'

interface Props {
  leads: Lead[]
}

export function LeadTable({ leads }: Props) {
  const navigate = useNavigate()

  if (leads.length === 0) {
    return (
      <div className="text-center py-16 text-slate-400">
        <p className="text-sm">No leads found.</p>
      </div>
    )
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50 border-b border-slate-200">
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Date</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Customer</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">Model</th>
            <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden sm:table-cell">Est. Total</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
            <th className="px-4 py-3" />
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {leads.map((lead) => (
            <tr
              key={lead.id}
              className="bg-white hover:bg-slate-50 cursor-pointer transition-colors"
              onClick={() => navigate(`/admin/leads/${lead.id}`)}
            >
              <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                {new Date(lead.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })}
              </td>
              <td className="px-4 py-3">
                <div className="font-medium text-slate-900">{lead.customer_name}</div>
                <div className="text-xs text-slate-400">{lead.email}</div>
              </td>
              <td className="px-4 py-3 text-slate-700 hidden md:table-cell">{lead.selected_model_name_snapshot}</td>
              <td className="px-4 py-3 text-right font-semibold text-slate-800 hidden sm:table-cell">
                {formatCurrency(lead.estimated_total_snapshot)}
              </td>
              <td className="px-4 py-3">
                <StatusBadge status={lead.status} />
              </td>
              <td className="px-4 py-3 text-right">
                <ExternalLink className="h-4 w-4 text-slate-400 inline-block" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
